import { useState, useContext, useEffect } from "react";
import { Modal } from "@material-ui/core";
import { useStyles } from "../Styles/styles";
import { UserContext } from "../Contexts/UserContext";

import { useLazyQuery, useMutation } from "@apollo/client";

import { GET_PERSON_RELATIONSHIPS } from "../Queries/GetPersonRelationships";
import PersonRefactor from "./PersonRefactor";
import RelationshipDropDown from "./RelationshipDropDown";
import { createEmptyRelationship } from "../utils/EmptyObjects";
import { formatNameFromPersonObj, handleUuid } from "../utils/utils";
import _ from "lodash";
import { REMOVE_RELATIONSHIP_FROM_PERSON } from "../Mutations/RemoveRelationshipInPerson";
import { ADD_OR_UPDATE_RELATIONSHIP_IN_PERSON } from "../Mutations/AddOrUpdateRelationshipInPerson";
import { ModalHeader } from "./ModalHeader";
import addButton from "../Assets/addButton.png";

const RelationshipModal = ({
  modalOpen,
  setModalOpen,
  personId,
  canEdit,
  setPausePolling,
  ...props
}) => {
  const classes = useStyles();
  const { user, responseErrorHandler, nightMode } = useContext(UserContext);
  const [data, setData] = useState(null);

  const [getPersonRelationships, { loading, error }] = useLazyQuery(
    GET_PERSON_RELATIONSHIPS,
    {
      variables: { originatorId: user?.id, personId: personId },
      fetchPolicy: "network-only",
      onError: error => {
        console.error(JSON.stringify(error));
      },
      onCompleted: data => {
        if (data) {
          responseErrorHandler(data);
          setData(data);
        }
      },
    }
  );
  const [removeRelationshipMutation] = useMutation(
    REMOVE_RELATIONSHIP_FROM_PERSON,
    {
      context: { serializationKey: "incidentQueue" },
    }
  );

  const [addOrUpdateRelationshipInPerson] = useMutation(
    ADD_OR_UPDATE_RELATIONSHIP_IN_PERSON,
    {
      context: { serializationKey: "incidentQueue" },

      onCompleted: data => {
        if (
          data &&
          data?.addOrUpdateRelationshipInPerson?.relationshipUuid?.includes(
            "Offline"
          )
        ) {
          //possibly update the uuid to an id here
          setData(prevState => {
            let clone = _.cloneDeep(prevState);
            clone.getPerson.relationships.map(r => {
              if (
                r.id === data.addOrUpdateRelationshipInPerson.relationshipUuid
              ) {
                r.id = data.addOrUpdateRelationshipInPerson.relationship.id;
              }
              return clone;
            });
            return clone;
          });
        }
      },
    }
  );

  const addEmptyRelationship = _.throttle(
    () => {
      const relationshipUuid = handleUuid();
      const personUuid = handleUuid();
      const personalAttributeUuid = handleUuid();
      const emptyRelationship = createEmptyRelationship(
        relationshipUuid,
        personUuid,
        personalAttributeUuid
      );
      setData(prevState => {
        let clone = _.cloneDeep(prevState);
        clone.getPerson.relationships.push(emptyRelationship);
        return clone;
      });

      addOrUpdateRelationshipInPerson({
        variables: {
          input: {
            originatorId: user?.id,
            targetId: data?.getPerson?.id,
            relationshipId: emptyRelationship?.id,
            personId: emptyRelationship?.person?.id,
          },
        },
      });
    },
    5000,
    { leading: true, trailing: true }
  );

  const addPersonToRelationship = (relationshipId, personId) => {
    //dispatch will be handled by the PersonRefactor component
    //mutation
    addOrUpdateRelationshipInPerson({
      variables: {
        input: {
          originatorId: user?.id,
          targetId: data?.getPerson?.id,
          relationshipId: relationshipId,
          personId: personId,
        },
      },
    });
  };
  const removeRelationship = relationshipId => {
    setData(prevState => {
      let clone = _.cloneDeep(prevState);
      clone.getPerson.relationships = clone.getPerson.relationships.filter(
        r => r?.id.toString() !== relationshipId?.toString()
      );
      return clone;
    });
    removeRelationshipMutation({
      variables: {
        input: {
          originatorId: user?.id,
          targetId: data?.getPerson?.id,
          relationshipId: relationshipId,
        },
      },
    });
    //add mutation to remove
  };

  useEffect(() => {
    if (data?.getPerson?.relationships?.length === 0) {
      addEmptyRelationship();
    }
  }, [data?.getPerson?.relationships, addEmptyRelationship]);

  useEffect(() => {
    if (modalOpen && !!personId && !personId.includes("Offline_")) {
      getPersonRelationships();
    }
  }, [modalOpen, getPersonRelationships, personId]);

  return (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      className={classes.modal}
      data-cy="address-history-modal"
      keepMounted={false}
    >
      <div
        id={"tabs-wrapper"}
        style={{
          height: "75vh",
          maxWidth: "80vw",
          overflow: "auto",
          borderRadius: "5px",
        }}
        className={classes.tabRoot}
      >
        <ModalHeader
          headerName={"Relationships"}
          setModalOpen={setModalOpen}
          nightMode={nightMode}
          helpIconObjectName={"mobile_relationships"}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flexWrap: "no-wrap",
            justifyContent: "space-between",
            alignItems: "left",
            width: "100%",
            border: "1px solid grey",
            borderRadius: "5px",
            padding: "5px",
          }}
          autoComplete="off"
        >
          <button
            type="button"
            onClick={addEmptyRelationship}
            style={{ background: "none", border: 0, padding: 0 }}
          >
            <img
              data-cy={"add-relationship-button"}
              src={addButton}
              className={classes.pngButton}
              alt="Add Relationship"
            />
          </button>
          {loading
            ? "Loading"
            : error
            ? `Error: ${error}`
            : data
            ? data.getPerson?.relationships?.map(relation => {
                return (
                  <span
                    key={`${relation?.relationshipType?.name}-${relation?.id}-for-${relation?.person?.id}`}
                  >
                    <h3>{formatNameFromPersonObj(relation?.person)}</h3>
                    <PersonRefactor
                      personData={relation?.person}
                      canEdit={canEdit}
                      userRoles={user?.roleNames}
                      userId={user?.id}
                      removePerson={removeRelationship}
                      containerId={relation?.id}
                      addPersonToRelationship={addPersonToRelationship}
                      setPausePolling={setPausePolling}
                    >
                      <RelationshipDropDown
                        startingValue={relation.relationshipType}
                        relationshipId={relation?.id}
                        targetId={data.getPerson?.id}
                        userId={user?.id}
                        userRoles={user?.roleNames}
                        disabled={false}
                        canEdit={props.canEdit}
                      />
                    </PersonRefactor>
                  </span>
                );
              })
            : `Something went wrong`}
        </div>
      </div>
    </Modal>
  );
};

export default RelationshipModal;
